import { useAuth } from '@platform/auth-client';
import { PageHeader } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { Truck } from 'lucide-react';
import { HistorySection } from '../history/HistorySection.tsx';
import { SetupProgressCard } from '../setup/SetupProgressCard.tsx';
import { TeamSection } from '../team/TeamSection.tsx';
import { usePageContext } from '../usePageContext.ts';
import { CarrierConnections } from './CarrierConnections.tsx';
import { canDispatchWith, CARRIERS_MANAGE_ROLE, useCarriers } from './carriers.ts';
import { OrderSettingsSection } from './OrderSettingsSection.tsx';
import { SenderSection } from './SenderSection.tsx';
import { SETTINGS_SECTIONS } from './settings-sections.ts';
import { SettingsSection } from './SettingsSection.tsx';
import { SubsidiariesSection } from './SubsidiariesSection.tsx';
import styles from './Settings.module.css';

export type SettingsPageProps = {
  api: TypedClient;
  shipping: ShippingClient;
  onOpenHistory: () => void;
  onOpenSetupStep: (step: number) => void;
};

export const SettingsPage = ({ api, shipping, onOpenHistory, onOpenSetupStep }: SettingsPageProps) => {
  const { t } = usePageContext();
  const canManage = useAuth().hasRole(CARRIERS_MANAGE_ROLE);
  const carriers = useCarriers(shipping);
  const items = carriers.data?.items ?? [];

  return (
    <>
      <PageHeader title={t('nav.settings')} context={t('settings.context')} />
      <SetupProgressCard api={api} shipping={shipping} whenComplete="review" onOpenStep={onOpenSetupStep} />
      <SettingsSection
        id={SETTINGS_SECTIONS.carriers}
        icon={Truck}
        title={t('carriers.title')}
        description={t('carriers.context')}
        action={
          carriers.data === undefined ? undefined : (
            <span className={styles['counter']}>
              {t('carriers.connectedCount', {
                count: items.filter(canDispatchWith).length,
                total: items.length,
              })}
            </span>
          )
        }
      >
        <CarrierConnections api={api} shipping={shipping} canManage={canManage} />
      </SettingsSection>
      <SenderSection shipping={shipping} />
      <OrderSettingsSection api={api} />
      <SubsidiariesSection api={api} />
      <TeamSection api={api} />
      <HistorySection onOpen={onOpenHistory} />
    </>
  );
};
